// ============================================================================
//  nhisSheet.js — read an NHIS release spreadsheet, and write the template for it
// ----------------------------------------------------------------------------
//  A release ships four sheets into four different tables. Each has its own
//  columns, so each gets an entry in SHEET_SPECS below; everything else — header
//  detection, cell coercion, tab selection, template generation — is shared.
//
//  The parsing happens in the browser on purpose. `exceljs` is already a
//  frontend dependency; the API host has no spreadsheet library, and adding one
//  to a deployment where `npm install` has not been run is how you get
//  MODULE_NOT_FOUND on a live server.
//
//  What this module does NOT do is derive anything. It hands back the raw cells
//  it found and the server works out the age band, the specialty code and the
//  rest for both preview and commit. That way the figures the operator approves
//  are the figures that get written; a bug here can only ever mis-read a cell,
//  never invent a tariff.
//
//  exceljs is ~1 MB, so it is pulled in by dynamic import(): the chunk is
//  fetched the first time someone picks a file or asks for a template, and never
//  on a page load that does neither.
// ============================================================================

let _excel = null;

async function excel() {
  if (_excel) return _excel;
  const mod = await import('exceljs');
  // package.json points `browser` at the prebuilt UMD bundle, which webpack may
  // surface either as the namespace itself or under .default depending on how it
  // interops. Both are handled rather than guessed at.
  _excel = mod.default || mod;
  return _excel;
}

// ── Cell values ─────────────────────────────────────────────────────────────
//  exceljs hands back seven different shapes depending on how the cell was
//  authored. Text pasted from a PDF is often rich text; a tariff computed in the
//  sheet is a formula object; a code typed as a link is a hyperlink object.
export function cellText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value.richText)) return value.richText.map((t) => t.text || '').join('');
  if ('result' in value) return cellText(value.result);       // formula
  if ('text' in value) return cellText(value.text);           // hyperlink
  if ('error' in value) return '';                            // #REF! and friends
  return String(value);
}

// Keeps a numeric cell numeric. Turning 33.31 into "33.31" and back loses
// nothing here, but a cell formatted as currency can stringify as "GH¢33.31" in
// one locale and "33.31 GH¢" in another, and the server should not have to care.
function rawNumberOrText(cell) {
  if (!cell) return '';
  const v = cell.value;
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && typeof v.result === 'number') return v.result;
  return cellText(v);
}

// ── Per-sheet column specifications ─────────────────────────────────────────
//
//  `patterns`   how to recognise each column from its header text
//  `anchorAny`  at least one of these must be found for a row to BE the header
//  `fallback`   column numbers to assume when no header row can be found
//  `numeric`    fields whose cell value is kept as a number when it is one
//  `expected`   fields the importer needs; anything missing is reported on screen
export const SHEET_SPECS = {
  services: {
    label: 'service tariffs',
    patterns: {
      // Anchored: the diagnosis sheet's "Core G-DRG" must NOT match this, or a
      // diagnosis sheet would be read as a service sheet.
      code:     /^\s*(?:g\s*-?\s*drg|gdrg|code|tariff\s*code)\s*$/i,
      // NHIS labels the name column with the patient type the sheet covers, so
      // "OUT PATIENT" and "IN PATIENT" are name headers as much as "Service".
      service:  /out\s*-?\s*patient|in\s*-?\s*patient|service|description|^\s*name\s*$/i,
      tariff:   /tariff|amount|price|rate|gh\s*[¢c]/i,
      specialtyCategory: /special|categor|\bmdc\b|group/i,
    },
    anchorAny: ['code'],
    fallback:  { code: 1, service: 2, tariff: 3 },
    numeric:   ['tariff'],
    expected:  ['code', 'service', 'tariff'],
    // A tariff sheet has no ICD codes. One that does is a diagnosis or procedure
    // sheet.
    foreign:   { 'an ICD-10 column': /icd/i },
  },

  //  ── INVESTIGATIONS ────────────────────────────────────────────────────────
  //  Three columns, and no tariff:
  //
  //    # | G-DRG CODE | INVESTIGATION NAME
  //    1 | INVE01D    | 2 Hour Post Prandial Blood Glucose
  //
  //  THE MISSING TARIFF IS THE POINT. The NHIA publishes which investigations exist
  //  and the G-DRG each is billed under; what a facility charges is the facility's
  //  own table. So `numeric` is empty and `tariff` is not a pattern at all — adding
  //  one "just in case" is what made this sheet fail 180 of 193 rows when it was
  //  dropped into the Services importer, which read INVESTIGATION NAME as the price.
  investigations: {
    label: 'investigations',
    patterns: {
      //  Anchored, so the diagnosis sheet's "Core G-DRG" and the procedure sheet's
      //  "G-DRG Group Name" cannot match. "#" is deliberately not a pattern: it is
      //  the sheet's own row counter and binding it to `code` would import 192 row
      //  numbers as G-DRGs.
      code: /^\s*(?:g\s*-?\s*drg(?:\s*code)?|gdrg|code|inve\s*code)\s*$/i,
      //  "INVESTIGATION NAME", and the words a facility's own copy might use.
      investigation: /investigat|^\s*test(?:\s*name)?\s*$|lab(?:oratory)?(?:\s*name)?|scan(?:\s*name)?|examination/i,
    },
    //  Either header identifies the sheet. The name column is the distinctive one —
    //  no other sheet here says "investigation" — so a copy that has renamed its code
    //  column is still recognised.
    anchorAny: ['code', 'investigation'],
    //  Column 1 is `#`. A headerless copy of this sheet still has the counter, so the
    //  fallback skips it rather than reading it as the code.
    fallback: { code: 2, investigation: 3 },
    numeric: [],
    expected: ['code', 'investigation'],
    //  An ICD column means this is a diagnosis or procedure sheet, not a test list.
    foreign: { 'an ICD-10 column': /icd/i },
  },

  diagnoses: {
    label: 'diagnoses',
    // TWO LAYOUTS, ONE SPEC
    //
    //  The NHIS release sheet:
    //    MDC Category | Core G-DRG | ICD-10 Code | Diagnosis / Indication | Level
    //
    //  The ICD-matched sheet, six columns and no Level:
    //    ICD-10 Category | ICD-10 Code | Diagnosis / Condition |
    //    G-DRG Code | NHIA Specialty / MDC | G-DRG Description
    //
    //  Every pattern below is written so it can tell the two apart, because the
    //  second layout has FOUR headers that the naive patterns matched wrongly:
    //
    //    "ICD-10 CATEGORY"    matched /icd/ and became the ICD-10 CODE column
    //    "ICD-10 CODE"        was then never bound at all
    //    "NHIA SPECIALTY/MDC" was dropped, mdcCategory having gone to column 1
    //    "G-DRG DESCRIPTION"  would match /g-?drg/ if it came first
    //
    //  Measured, not supposed: read as it stood, all 6,537 rows of that sheet
    //  imported with "Certain infectious and parasitic diseases (A00-B99)" as the
    //  ICD-10 code, and `expected` reported nothing missing — because it checks
    //  that a column was FOUND, never that a different one was. `collidedColumns`
    //  in readSheet is the general guard against that.
    patterns: {
      // "MDC Category", "NHIA Specialty / MDC" — but NOT "ICD-10 Category", which
      // is the ICD chapter and belongs to icdCategory below.
      mdcCategory: /^(?!.*\bicd\b).*(?:\bmdc\b|categor|special)/i,
      // "Core G-DRG", "G-DRG Code" — but NOT "G-DRG Description", and not the
      // procedure sheet's "G-DRG Group Name".
      core:        /^(?!.*(?:descript|group|procedure)).*(?:core|g\s*-?\s*drg|gdrg)/i,
      // "ICD-10 Code" — but NOT "ICD-10 Category".
      icd:         /^(?!.*categor).*icd/i,
      name:        /diagnos|indication|condition/i,
      level:       /level/i,
      // OPTIONAL — not in `expected`. NHIS publishes this sheet without a price,
      // but a facility's own copy often adds one, and when it does that figure is
      // written. Absent, the tariff is left alone entirely.
      tariff:      /tariff|amount|price|rate|gh\s*[¢c]/i,
      // OPTIONAL, both from the six-column layout. Neither has a column in
      // diagnosis_tarf, and neither is invented into one — they are read because
      // they answer questions the other columns cannot:
      //
      //   icdCategory  the ICD-10 chapter, shown in the preview so an operator can
      //                see what a bare code is.
      //   gdrgName     "Diarrhoea and Vomiting >=12 Yrs". THE AGE BAND LIVES HERE.
      //                The diagnosis name on this sheet is just "Cholera", so
      //                reading the band off the name — which is all the five-column
      //                layout could do — files 98.7% of the sheet as "Both". That
      //                is not cosmetic: age_cat gates the diagnosis loader, so it
      //                offers a paediatric G-DRG for a 40-year-old.
      icdCategory: /icd.*categor|categor.*icd/i,
      gdrgName:    /(?:g\s*-?\s*drg|gdrg).*(?:descript|name)/i,
    },
    // Either identifies a diagnosis header. If a SERVICE sheet is picked here by
    // mistake its "G-DRG" header matches `core`, so `expected` below is what
    // actually catches the mix-up: no ICD column, reported on screen.
    anchorAny: ['icd', 'core'],
    fallback:  { mdcCategory: 1, core: 2, icd: 3, name: 4, level: 5 },
    numeric:   ['tariff'],
    expected:  ['core', 'icd', 'name'],
    // The one that cannot be caught by a missing column: a PROCEDURE sheet has
    // every column a diagnosis sheet needs, plus this one.
    foreign:   { 'a procedure-name column': /procedure|group\s*name/i },
  },

  procedures: {
    label: 'procedures',
    patterns: {
      mdcCategory: /\bmdc\b|categor/i,
      // "Core G-DRG" and "Procedure / G-DRG Group Name" BOTH contain "G-DRG", and
      // they sit next to each other. Detection happens in column order so the
      // left one would win by accident — this makes it deliberate instead: a cell
      // mentioning a procedure or a group is never the Core G-DRG column, whatever
      // order the sheet puts them in.
      core:        /^(?!.*(?:procedure|group)).*(?:core|g\s*-?\s*drg|gdrg)/i,
      procedure:   /procedure|group\s*name/i,
      icd:         /icd/i,
      indication:  /diagnos|indication/i,
      level:       /level/i,
    },
    // The procedure name column is what makes this a procedure sheet. A DIAGNOSIS
    // sheet has core + icd + a diagnosis column but no procedure column, so
    // `expected` catches that mix-up the same way.
    anchorAny: ['procedure', 'core'],
    fallback:  { mdcCategory: 1, core: 2, procedure: 3, icd: 4, indication: 5, level: 6 },
    numeric:   [],
    expected:  ['core', 'procedure'],
    // A procedure carries no price on this sheet; one that does is a tariff sheet.
    foreign:   { 'a tariff column': /tariff|gh\s*[¢c]/i },
  },

  medications: {
    label: 'medicines',
    patterns: {
      code:    /^\s*code\s*$|drug\s*code|item\s*code/i,
      // "Generic Name, Dosage Form, Strength" — the strength lives inside this
      // one column, which is why the dose unit has to be deduced from it.
      name:    /generic|dosage\s*form|strength|medicine|drug\s*name|^\s*name\s*$/i,
      pricing: /unit\s*of\s*pricing|pricing|unit(?!\s*of\s*measure)/i,
      price:   /price|cost|tariff|gh\s*[¢c]/i,
      level:   /level/i,
    },
    // The code column is the anchor. A service sheet's "G-DRG" header does not
    // match it, and neither does a diagnosis sheet's "ICD-10 Code" — both are
    // qualified, this pattern is anchored on the bare word.
    anchorAny: ['code', 'name'],
    fallback:  { code: 1, name: 2, pricing: 3, price: 4, level: 5 },
    numeric:   ['price'],
    expected:  ['code', 'name', 'price'],
    // A medicines list has no diagnostic coding on it.
    foreign:   { 'an ICD-10 column': /icd/i, 'a G-DRG column': /g\s*-?\s*drg|gdrg/i },
  },
};

// Human names for the columns, used in the "not found" message.
const FIELD_LABELS = {
  code: 'G-DRG', service: 'Service name', tariff: 'Tariff',
  specialtyCategory: 'Specialty category',
  mdcCategory: 'MDC category', core: 'Core G-DRG', icd: 'ICD-10 code',
  name: 'Diagnosis', level: 'Level of healthcare',
  icdCategory: 'ICD-10 category', gdrgName: 'G-DRG description',
  procedure: 'Procedure name', indication: 'Diagnosis / indication',
  pricing: 'Unit of pricing', price: 'Price',
  //  Its own key rather than reusing the diagnosis sheet's `name`: these labels are
  //  what the "column not found" message says, and "Diagnosis is missing" on an
  //  investigations sheet sends the operator looking for the wrong column.
  investigation: 'Investigation name',
};

// ── Header detection ────────────────────────────────────────────────────────
//  The sheets are not machine-generated and the header is not always on row 1 —
//  a title and a blank line above it are common. So the first 12 rows are
//  scanned for a row that looks like the header for this kind of sheet.
function detectHeader(sheet, spec) {
  const limit = Math.min(12, sheet.rowCount || 0);
  for (let r = 1; r <= limit; r += 1) {
    const row = sheet.getRow(r);
    const found = {};
    row.eachCell({ includeEmpty: false }, (cell, col) => {
      const text = cellText(cell.value).trim();
      if (!text) return;
      for (const [key, re] of Object.entries(spec.patterns)) {
        if (found[key] === undefined && re.test(text)) found[key] = col;
      }
    });
    if (spec.anchorAny.some((k) => found[k] !== undefined)) {
      return { headerRow: r, columns: found };
    }
  }
  return null;
}

// Sheets that are documentation, samples or cover pages rather than data. They
// are still READ — the operator can pick any tab and see what is on it — but they
// are kept out of the automatic selection, because the alternative is what this
// caught in testing: an unfilled template auto-selects its own "Example" tab, and
// one careless Commit imports specimen rows into a live catalogue.
const NON_DATA_SHEET = /how\s*to|guide|instruction|read\s*me|example|cover|do\s*not|lookup|^notes?$/i;

// ── One worksheet ───────────────────────────────────────────────────────────
function readSheet(sheet, spec) {
  const detected = detectHeader(sheet, spec);

  const headerRow = detected ? detected.headerRow : 0;
  const columns = { ...(detected ? detected.columns : spec.fallback) };

  // When no header was found at all, the fallback layout applies wholesale.
  if (!detected) Object.assign(columns, spec.fallback);

  // A column the importer needs but could not find IN THE HEADER. Deliberately
  // computed from `detected` and not from `columns`: with no header at all the
  // fallback supplies a position for every field, so asking `columns` would
  // report nothing missing for a sheet of the wrong kind entirely. When there is
  // no header the `headerDetected` flag is the warning that matters.
  const missing = detected
    ? spec.expected.filter((f) => detected.columns[f] === undefined).map((f) => FIELD_LABELS[f] || f)
    : [];

  // TWO FIELDS BOUND TO ONE COLUMN
  //
  //  detectHeader takes the first pattern that matches each header, and nothing
  //  stopped two different fields matching the SAME header. When that happens the
  //  real column for one of them is never read, and `missing` above cannot see it:
  //  the field WAS found, just on the wrong column.
  //
  //  That is not hypothetical. "ICD-10 CATEGORY" matched both mdcCategory and icd,
  //  so the ICD-10 code of every row on a 6,537-row sheet was a chapter heading and
  //  the importer reported no problem at all. The patterns are now written to tell
  //  those apart, but a new sheet with a new header wording would do it again — so
  //  the collision is reported as a fact about the file rather than trusted not to
  //  recur.
  const collided = [];
  if (detected) {
    const byColumn = new Map();
    for (const [field, col] of Object.entries(detected.columns)) {
      if (!byColumn.has(col)) byColumn.set(col, []);
      byColumn.get(col).push(field);
    }
    for (const [col, fieldsOnIt] of byColumn) {
      if (fieldsOnIt.length < 2) continue;
      collided.push({
        column: col,
        header: cellText(sheet.getRow(detected.headerRow).getCell(col).value).trim(),
        fields: fieldsOnIt.map((f) => FIELD_LABELS[f] || f),
      });
    }
  }

  // Columns that belong to a DIFFERENT kind of sheet. A procedure sheet contains
  // everything a diagnosis sheet needs — MDC, Core G-DRG, ICD-10, a diagnosis
  // column — plus a procedure column, so reading one as the other finds no
  // missing columns and imports the indications as diagnoses. The giveaway is the
  // extra column, so that is what gets reported.
  const foreign = [];
  if (detected && spec.foreign) {
    const header = sheet.getRow(detected.headerRow);
    for (const [label, re] of Object.entries(spec.foreign)) {
      let hit = false;
      header.eachCell({ includeEmpty: false }, (cell) => {
        if (!hit && re.test(cellText(cell.value).trim())) hit = true;
      });
      if (hit) foreign.push(label);
    }
  }

  const fields = Object.keys(spec.patterns);
  const rows = [];
  let skippedBlank = 0;

  for (let r = headerRow + 1; r <= (sheet.rowCount || 0); r += 1) {
    const row = sheet.getRow(r);
    const out = { rowNumber: r };
    let anyValue = false;

    for (const f of fields) {
      const col = columns[f];
      if (col === undefined) { out[f] = ''; continue; }
      const cell = row.getCell(col);
      const v = spec.numeric.includes(f) ? rawNumberOrText(cell) : cellText(cell.value).trim();
      out[f] = v;
      if (v !== '' && v !== null && v !== undefined) anyValue = true;
    }

    // Entirely empty lines are the spacer rows between sections and are not worth
    // reporting. A row with SOMETHING in it is kept even if incomplete — the
    // server flags it, so the operator sees the problem in their own file instead
    // of the importer quietly dropping a row.
    if (!anyValue) { skippedBlank += 1; continue; }

    rows.push(out);
  }

  return {
    name: sheet.name,
    headerRow,
    headerDetected: Boolean(detected),
    missingColumns: missing,
    foreignColumns: foreign,
    collidedColumns: collided,
    // Which column each field was read from. The operator's only way to check that
    // "ICD-10 Code" was taken from the ICD-10 Code column and not from something
    // else that happened to mention ICD.
    //
    // Keyed by the FIELD name, not FIELD_LABELS: that map is shared by all four
    // specs, so `code` reads "G-DRG" and `name` reads "Diagnosis" on a MEDICINES
    // sheet. A confidently wrong label is worse here than a technical one, and the
    // screen that shows this has its own wording for the fields it cares about.
    boundColumns: Object.fromEntries(
      Object.entries(columns).filter(([, col]) => col !== undefined)
    ),
    // Only meaningful for services; harmless elsewhere.
    hasCategoryColumn: columns.specialtyCategory !== undefined,
    looksLikeData: !NON_DATA_SHEET.test(sheet.name),
    rows,
    skippedBlank,
  };
}

// Which tab to open on. A data tab with a real header wins, then any data tab
// with rows on it.
//
// There is deliberately NO "any sheet with rows" fallback. That tier existed and
// undid the whole point: for a downloaded template the data tab is empty, so the
// fallback promoted the "Example (do not fill)" tab to the default and put
// specimen rows one click from being committed. When nothing looks like a data
// sheet the answer is sheet 0 and an empty picker — the operator can still select
// any tab by hand, which is a decision rather than a default.
export function preferredSheetIndex(sheets = []) {
  const byQuality = [
    (s) => s.rows.length > 0 && s.looksLikeData && s.headerDetected && !s.missingColumns.length,
    (s) => s.rows.length > 0 && s.looksLikeData && s.headerDetected,
    (s) => s.rows.length > 0 && s.looksLikeData,
  ];
  for (const test of byQuality) {
    const i = sheets.findIndex(test);
    if (i >= 0) return i;
  }
  return 0;
}

// ── Public: parse a picked file ─────────────────────────────────────────────
//  Every worksheet is read, not just the first. NHIS splits out-patient and
//  in-patient tariffs onto separate tabs of one workbook, and the operator has to
//  be able to say which tab they are importing.
export async function parseWorkbook(file, kind = 'services') {
  const spec = SHEET_SPECS[kind];
  if (!spec) throw new Error(`No column mapping for "${kind}" sheets`);

  const ExcelJS = await excel();
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const sheets = [];
  wb.eachSheet((sheet) => {
    // A hidden sheet is usually a lookup table or a leftover, never the data.
    if (sheet.state === 'veryHidden') return;
    sheets.push(readSheet(sheet, spec));
  });

  if (!sheets.length) throw new Error('That workbook has no readable sheets.');
  return { fileName: file.name, kind, sheets };
}

// ── Templates ───────────────────────────────────────────────────────────────
//  Two sheets plus a guide. The data sheet is EMPTY apart from its header —
//  examples live on their own sheet so they cannot be imported by someone who
//  forgot to delete the sample rows.
//
//  The guide spells out which columns are read and which are worked out, because
//  the person filling it in is usually not the person who will run the import.
const NAVY = 'FF123A6D';

const TEMPLATES = {
  services: {
    fileName: 'NHIS Service Tariff Template.xlsx',
    sheetName: 'Services',
    headers: [
      { header: 'G-DRG',              key: 'code',     width: 14 },
      { header: 'SERVICE NAME',       key: 'service',  width: 56 },
      { header: 'TARIFF (GH¢)',       key: 'tariff',   width: 16, money: true },
      { header: 'SPECIALTY CATEGORY', key: 'category', width: 24 },
    ],
    textColumns: ['code'],
    examples: [
      ['OPDC06A', 'General OPD - Adult',                     19.44, 'GENERAL OPD'],
      ['OPDC06C', 'General OPD - Child',                     19.44, 'GENERAL OPD'],
      ['OPDC02A', 'Antenatal/Postnatal',                     33.31, 'GENERAL OPD'],
      ['ZOOM01A', 'Endoscopy (Rigid & Flexible) >=12 Yrs',   143.74, 'CROSS MDC'],
      ['ZOOM01C', 'Endoscopy (Rigid & Flexible) <12 Yrs',    134.85, 'CROSS MDC'],
    ],
    guide: [
      ['NHIS SERVICE TARIFF — HOW TO FILL THIS IN', ''],
      ['', ''],
      ['Fill in the "Services" sheet. One row per G-DRG. Do not rename the headers.', ''],
      ['', ''],
      ['COLUMN', 'WHAT TO PUT IN IT'],
      ['G-DRG', 'The NHIS tariff code, e.g. OPDC06A. Required. Must not repeat.'],
      ['SERVICE NAME', 'The service exactly as NHIS words it, e.g. "General OPD - Adult". Required.'],
      ['TARIFF (GH¢)', 'The price. 19.44 or GH¢19.44 both work. Required, may be 0.'],
      ['SPECIALTY CATEGORY', 'Optional. e.g. GENERAL OPD. Leave blank and the system'],
      ['', 'reuses whatever category it already holds for that code prefix.'],
      ['', ''],
      ['WORKED OUT FOR YOU — DO NOT ADD COLUMNS FOR THESE', ''],
      ['Age band', 'Read from the service name. "Adult" or ">=12 Yrs" becomes Adult,'],
      ['', '"Child" or "<12 Yrs" becomes Child, anything else becomes Both.'],
      ['Insurance', 'Always National Health Insurance Authority.'],
      ['Charge type', 'Always Main Services.'],
      ['Specialty code', 'The leading letters of the G-DRG, e.g. OPDC06A becomes OPDC.'],
      ['Description', 'Copied from the service name.'],
      ['Top-up', 'Left alone. Set per facility on the Services Setup screen — an'],
      ['', 'import never overwrites a top-up you have configured.'],
      ['', ''],
      ['BEFORE ANYTHING IS SAVED', ''],
      ['', 'The importer shows you every price that would change, and every row it'],
      ['', 'could not read, and waits for you to confirm. Nothing is written until'],
      ['', 'you press Commit.'],
    ],
    boldGuideRows: [5, 12, 22],
  },

  investigations: {
    fileName: 'NHIA Investigations Template.xlsx',
    sheetName: 'Investigations',
    //  THREE COLUMNS, AND NO TARIFF — the same shape the NHIA publishes. The `#` is
    //  kept because every copy of this sheet in the wild has it and an operator
    //  comparing the two should see the same thing; the importer ignores it.
    headers: [
      { header: '#',                  key: 'row',           width: 6 },
      { header: 'G-DRG CODE',         key: 'code',          width: 14 },
      { header: 'INVESTIGATION NAME', key: 'investigation', width: 56 },
    ],
    textColumns: ['code'],
    examples: [
      [1, 'INVE01D', '2 Hour Post Prandial Blood Glucose'],
      [2, 'INVE09D', 'BF for MPs'],
      [3, 'INVE52D', 'Full Blood Count'],
      [4, 'INVE34D', 'CT Scan - 1 Region + No Contrast'],
      [5, 'INVE30E', 'Ultrasound'],
    ],
    guide: [
      ['NHIA INVESTIGATIONS — HOW TO FILL THIS IN', ''],
      ['', ''],
      ['Fill in the "Investigations" sheet. One row per test. Do not rename the headers.', ''],
      ['', ''],
      ['COLUMN', 'WHAT TO PUT IN IT'],
      ['#', 'Optional. The sheet\'s own counter. Ignored by the importer.'],
      ['G-DRG CODE', 'The NHIA code, e.g. INVE01D. Required. Must not repeat.'],
      ['INVESTIGATION NAME', 'The test exactly as NHIA words it. Required.'],
      ['', ''],
      ['THERE IS NO PRICE COLUMN, AND THAT IS DELIBERATE', ''],
      ['', 'NHIA publishes which investigations exist and the G-DRG each is billed'],
      ['', 'under. What YOUR facility charges is your own table, and this import'],
      ['', 'never writes a price or a top-up — not even on a test it adds.'],
      ['', ''],
      ['WHAT THE IMPORT ACTUALLY DOES FOR YOU', ''],
      ['Fills in codes', 'A test you already price but have no G-DRG for cannot be'],
      ['', 'billed — the claim screen will not offer it at all. Matching by'],
      ['', 'name, this import fills that code in and the test becomes'],
      ['', 'billable. Your price is untouched.'],
      ['Routes labs and scans', 'CT, MRI, ultrasound and X-ray go to the scan table; bench'],
      ['', 'tests go to the lab table. The preview shows which, per row,'],
      ['', 'before anything is written.'],
      ['Adds what is missing', 'Tests you do not have yet arrive with NO PRICE. Choose'],
      ['', '"Codes only" on the commit step if you would rather add'],
      ['', 'nothing and only fill in the codes you are missing.'],
      ['', ''],
      ['BEFORE ANYTHING IS SAVED', ''],
      ['', 'The importer shows you every row it would add, every code it would'],
      ['', 'fill in, and every row it could not read, and waits for you to'],
      ['', 'confirm. Nothing is written until you press Commit.'],
    ],
    boldGuideRows: [5, 10, 15, 26],
  },

  diagnoses: {
    fileName: 'NHIS Diagnosis Template.xlsx',
    sheetName: 'Diagnoses',
    headers: [
      { header: 'MDC CATEGORY',           key: 'mdc',   width: 18 },
      { header: 'CORE G-DRG',             key: 'core',  width: 14 },
      { header: 'ICD-10 CODE',            key: 'icd',   width: 14 },
      { header: 'DIAGNOSIS / INDICATION', key: 'name',  width: 62 },
      { header: 'LEVEL OF HEALTHCARE',    key: 'level', width: 20 },
      // Optional, and last so a five-column sheet still lines up with the
      // template. Filled in, it becomes the diagnosis tariff; left blank, the
      // importer does not touch the tariff at all.
      { header: 'TARIFF (GH¢)',           key: 'tariff', width: 16, money: true },
    ],
    // Both are codes. Without this, Excel turns an ICD code like A01 into a
    // number in some locales, and a G-DRG like 1E05 into scientific notation.
    textColumns: ['core', 'icd', 'level'],
    examples: [
      ['Medical', 'MEDI26', 'K27',   'Peptic Ulcer',              '1,2,3', ''],
      ['Medical', 'MEDI26', 'B83.9', 'Helminthiasis',             '1,2,3', ''],
      ['Medical', 'MEDI27', 'R57.0', 'Cardiogenic Shock',         '1,2,3', ''],
      ['Medical', 'MEDI27', 'A41.9', 'Septicaemia, unspecified',  '1,2,3', ''],
      ['Medical', 'MEDI28', 'B54',   'Malaria',                   '1,2,3', 40.00],
    ],
    guide: [
      ['NHIS DIAGNOSES — HOW TO FILL THIS IN', ''],
      ['', ''],
      ['Fill in the "Diagnoses" sheet. One row per ICD-10 code per G-DRG.', ''],
      ['Do not rename the headers.', ''],
      ['', ''],
      ['COLUMN', 'WHAT TO PUT IN IT'],
      ['MDC CATEGORY', 'The major diagnostic category, e.g. Medical, Surgical,'],
      ['', 'Obstetrics and Gynaecology. Stored as both the Category and the'],
      ['', 'Specialty on the diagnosis.'],
      ['CORE G-DRG', 'The G-DRG the diagnosis is billed on, e.g. MEDI26. Required.'],
      ['ICD-10 CODE', 'e.g. K27 or B83.9. Required.'],
      ['DIAGNOSIS / INDICATION', 'The diagnosis exactly as NHIS words it. Required.'],
      ['LEVEL OF HEALTHCARE', 'Which facility levels may bill it, e.g. 1,2,3.'],
      ['TARIFF (GH¢)', 'Optional. NHIS does not publish a price on this sheet — a'],
      ['', 'diagnosis is billed on its G-DRG — but if your copy has one, put it'],
      ['', 'here and it is saved as the diagnosis tariff. Leave the WHOLE column'],
      ['', 'blank and the importer does not touch the tariff at all, so prices'],
      ['', 'already set on Diagnosis Setup are safe.'],
      ['', ''],
      ['THIS IS NOT THE ONLY LAYOUT ACCEPTED', ''],
      ['', 'An ICD-matched sheet imports too, and needs no rearranging. Its columns'],
      ['', 'are ICD-10 CATEGORY, ICD-10 CODE, DIAGNOSIS / CONDITION, G-DRG CODE,'],
      ['', 'NHIA SPECIALTY / MDC and G-DRG DESCRIPTION, in any order. It has no'],
      ['', 'Level column, so Level is left blank on those rows.'],
      ['', ''],
      ['', 'On that layout the age band is read from the G-DRG DESCRIPTION, not the'],
      ['', 'diagnosis name — "Cholera" says nothing about an age, but the G-DRG'],
      ['', '"Diarrhoea and Vomiting >=12 Yrs" does. The specialty is used as well,'],
      ['', 'so "Paediatric Surgery" is enough on its own.'],
      ['', ''],
      ['THE SAME ICD-10 CODE MAY APPEAR MORE THAN ONCE', ''],
      ['', 'A41.9 is billed under MEDI27 and again under MEDI30, with a different'],
      ['', 'wording each time. That is correct and both rows are kept. What is NOT'],
      ['', 'allowed is the same ICD-10 code twice under the SAME G-DRG.'],
      ['', ''],
      ['', 'It is common, not exceptional: on the ICD-matched sheet two thirds of'],
      ['', 'the ICD-10 codes carry more than one G-DRG. Some are the adult/child'],
      ['', 'pair, some are two ways of treating one condition — an abscess drained'],
      ['', 'percutaneously or by laparotomy — and both are real tariffs.'],
      ['', ''],
      ['WHEN THE SHEET AND THE CATALOGUE DISAGREE, YOU ARE ASKED', ''],
      ['', 'If your sheet bills a diagnosis on a G-DRG the catalogue does not hold'],
      ['', 'for it, that row is NOT written until you say what to do. The importer'],
      ['', 'shows you what the catalogue already has beside what your sheet adds,'],
      ['', 'and you choose: keep both, or leave it out. You can answer a whole'],
      ['', 'group at once — the adult/child pairs in one press, say — and Commit'],
      ['', 'stays disabled while any are still unanswered, so an import can never'],
      ['', 'quietly leave rows behind.'],
      ['', ''],
      ['WORKED OUT FOR YOU — DO NOT ADD COLUMNS FOR THESE', ''],
      ['Age band', 'From the G-DRG DESCRIPTION and the specialty when your sheet has'],
      ['', 'them, and from the diagnosis name when it does not. "Child", "<12 Yrs"'],
      ['', 'or "Paediatrics" becomes Child; "Adult" or ">=12 Yrs" becomes Adult;'],
      ['', 'anything else becomes Both. If the name and the G-DRG disagree, the'],
      ['', 'G-DRG wins — it is what the claim is billed on — and the preview says so.'],
      ['Insurance', 'Always National Health Insurance Authority.'],
      ['Specialty code', 'The leading letters of the G-DRG, e.g. MEDI26 becomes MEDI.'],
      ['Top-up', 'Always 0, and never overwritten. A top-up is what the facility'],
      ['', 'adds on, not something the authority publishes, so an import has'],
      ['', 'nothing to say about it. Set it on the Diagnosis Setup screen.'],
      ['', ''],
      ['A NOTE ON TRAILING COMMAS', ''],
      ['', 'NHIS sometimes writes "Septicaemia," or "Pneumonia," with a trailing'],
      ['', 'comma. Names are stored exactly as written, so "Pneumonia," and'],
      ['', '"Pneumonia" become two separate diagnoses. The importer warns you when'],
      ['', 'that is about to happen — tidy the sheet if they should be one.'],
      ['', ''],
      ['BEFORE ANYTHING IS SAVED', ''],
      ['', 'The importer shows you every row it would add or change, and every row'],
      ['', 'it could not read, and waits for you to confirm. Nothing is written'],
      ['', 'until you press Commit.'],
    ],
    // The section headings, by row. Recounted whenever the guide changes — they
    // are positional, so an inserted line silently bolds a blank row and leaves a
    // heading plain.
    boldGuideRows: [6, 20, 31, 41, 50, 62, 68],
  },

  procedures: {
    fileName: 'NHIS Procedure Template.xlsx',
    sheetName: 'Procedures',
    headers: [
      { header: 'MDC CATEGORY',                 key: 'mdc',        width: 26 },
      { header: 'CORE G-DRG',                   key: 'core',       width: 14 },
      { header: 'PROCEDURE / G-DRG GROUP NAME', key: 'procedure',  width: 40 },
      { header: 'ICD-10 CODE',                  key: 'icd',        width: 14 },
      { header: 'DIAGNOSIS / INDICATION',       key: 'indication', width: 46 },
      { header: 'LEVEL OF HEALTHCARE',          key: 'level',      width: 20 },
    ],
    textColumns: ['core', 'icd', 'level'],
    examples: [
      ['Obstetrics and Gynecology', 'OBGY01', 'Gynaecological Laparotomy', 'N80',   'Endometriosis', '1,2,3'],
      ['Obstetrics and Gynecology', 'OBGY01', 'Gynaecological Laparotomy', 'O00.9', 'Ectopic pregnancy', '1,2,3'],
      ['Obstetrics and Gynecology', 'OBGY01', 'Gynaecological Laparotomy', 'O85',   'Puerperal sepsis', '1,2,3'],
      ['Obstetrics and Gynecology', 'OBGY02', 'Diagnostic laparoscopy',    'N73.9', 'Pelvic Inflammatory diseases', '1,2,3'],
      ['Obstetrics and Gynecology', 'OBGY05', 'Cauterization',             'N72',   'Inflammatory disease of cervix uteri/vulva', '1,2,3'],
    ],
    guide: [
      ['NHIS PROCEDURES — HOW TO FILL THIS IN', ''],
      ['', ''],
      ['Fill in the "Procedures" sheet. Repeat the procedure on as many rows as it', ''],
      ['has indications — exactly as the NHIS booklet lays it out.', ''],
      ['', ''],
      ['COLUMN', 'WHAT TO PUT IN IT'],
      ['MDC CATEGORY', 'e.g. Obstetrics and Gynecology. Becomes the specialty, and the'],
      ['', 'procedure group as "MDC: OBSTETRICS AND GYNECOLOGY".'],
      ['CORE G-DRG', 'The G-DRG for the procedure group, e.g. OBGY01. Required.'],
      ['PROCEDURE / G-DRG GROUP NAME', 'e.g. Gynaecological Laparotomy. Required.'],
      ['ICD-10 CODE', 'The code that indicates this procedure, e.g. N80. Optional.'],
      ['DIAGNOSIS / INDICATION', 'What that code is, e.g. Endometriosis. Optional.'],
      ['LEVEL OF HEALTHCARE', 'Which facility levels may bill it, e.g. 1,2,3.'],
      ['', ''],
      ['ONE PROCEDURE, MANY ROWS', ''],
      ['', 'OBGY01 "Gynaecological Laparotomy" is listed against eleven ICD-10'],
      ['', 'codes, so it takes eleven rows here. It becomes ONE procedure in the'],
      ['', 'catalogue, because a procedure is priced once, not once per diagnosis.'],
      ['', 'The importer tells you how many rows became how many procedures'],
      ['', 'before you commit, so the reduction is never a surprise.'],
      ['', ''],
      ['', 'The ICD-10 codes are kept in the import record so nothing is lost, but'],
      ['', 'the procedure table has no column for them. Filtering procedures by'],
      ['', 'their indications needs a table of its own and is not built yet.'],
      ['', ''],
      ['WORKED OUT FOR YOU — DO NOT ADD COLUMNS FOR THESE', ''],
      ['Age band', 'Read from the procedure name. "Child" or "<12 Yrs" becomes Child,'],
      ['', '"Adult" or ">=12 Yrs" becomes Adult, anything else becomes Both.'],
      ['Insurance', 'Always National Health Insurance Authority.'],
      ['Specialty code', 'The leading letters of the G-DRG, e.g. OBGY01 becomes OBGY.'],
      ['Tariff and top-up', 'NOT on this sheet. Both start at 0 and are set per facility on'],
      ['', 'the Procedure Setup screen. An import never overwrites them.'],
      ['', ''],
      ['BEFORE ANYTHING IS SAVED', ''],
      ['', 'The importer shows you every procedure it would add or change, and'],
      ['', 'every row it could not read, and waits for you to confirm. Nothing is'],
      ['', 'written until you press Commit.'],
    ],
    boldGuideRows: [6, 15, 26, 34],
  },

  medications: {
    fileName: 'NHIS Medicines Template.xlsx',
    sheetName: 'Medicines',
    headers: [
      { header: 'CODE',                                key: 'code',    width: 14 },
      { header: 'GENERIC NAME, DOSAGE FORM, STRENGTH', key: 'name',    width: 58 },
      { header: 'UNIT OF PRICING',                     key: 'pricing', width: 18 },
      { header: 'PRICE (GHC)',                         key: 'price',   width: 14, money: true },
      { header: 'LEVEL OF PRESCRIBING',                key: 'level',   width: 20 },
    ],
    textColumns: ['code', 'level'],
    examples: [
      ['ACETAZIN1', 'Acetazolamide Injection, 500 mg',    'Ampoule', 17.16,  'C'],
      ['ACICLOCR1', 'Acyclovir Cream, 5%',                '5G',      38.50,  'C'],
      ['ACICLOSU2', 'Acyclovir Suspension, 200 mg/5 mL',  '20 mL',   276.91, 'B2'],
      ['ACTCHAPO1', 'Activated Charcoal Powder, 50 g',    '50 G',    38.56,  'A'],
      ['ADRENAIN2', 'Adrenaline Injection, 1:10,000',     'Vial',    6.55,   'M'],
    ],
    guide: [
      ['NHIS MEDICINES — HOW TO FILL THIS IN', ''],
      ['', ''],
      ['Fill in the "Medicines" sheet. One row per code. Do not rename the headers.', ''],
      ['', ''],
      ['COLUMN', 'WHAT TO PUT IN IT'],
      ['CODE', 'The NHIS medicine code, e.g. ACETAZIN1. Required, must not repeat.'],
      ['GENERIC NAME, DOSAGE FORM, STRENGTH', 'All three in one cell, as NHIS writes it:'],
      ['', '"Acetazolamide Injection, 500 mg". Required.'],
      ['UNIT OF PRICING', 'What the price is per, e.g. Ampoule, Tablet, 20 mL, 1 Course.'],
      ['PRICE (GHC)', 'The price. 17.16 or GH¢17.16 both work. Required, may be 0.'],
      ['LEVEL OF PRESCRIBING', 'Which prescriber may order it: A, B1, B2, C or M.'],
      ['', ''],
      ['THE DOSE UNIT IS WORKED OUT FROM THE NAME', ''],
      ['', 'There is no strength column — the strength is the end of the name, and'],
      ['', 'the system reads it out. The number and its unit are always separated'],
      ['', 'by one space, so "5%" is stored as "5 %" and "50g" as "50 g".'],
      ['', ''],
      ['', '  Acetazolamide Injection, 500 mg     ->  500 mg'],
      ['', '  Acyclovir Cream, 5%                 ->  5 %'],
      ['', '  Acyclovir Injection, 250 mg vial    ->  250 mg'],
      ['', '  Adrenaline Injection, 1:10,000      ->  1:10,000'],
      ['', '  Iron Polymaltose Complex Capsule    ->  1   (no strength in the name)'],
      ['', ''],
      ['', 'Every deduced dose unit is shown in the preview before anything is'],
      ['', 'saved. Correct any of them afterwards on the Stores screen.'],
      ['', ''],
      ['WORKED OUT FOR YOU — DO NOT ADD COLUMNS FOR THESE', ''],
      ['Insurance', 'Always National Health Insurance Authority.'],
      ['Reorder level', 'Starts at 100.'],
      ['Top-up', 'Starts at 0.'],
      ['Product type', 'Always "drug".'],
      ['', ''],
      ['', 'Reorder level and top-up are yours to set afterwards. A later import'],
      ['', 'refreshes the name, unit of pricing, price and dose unit only — it'],
      ['', 'never puts your reorder levels or top-ups back to the defaults.'],
      ['', ''],
      ['BEFORE ANYTHING IS SAVED', ''],
      ['', 'The importer shows you every price that would change, and every row it'],
      ['', 'could not read, and waits for you to confirm. Nothing is written until'],
      ['', 'you press Commit.'],
    ],
    boldGuideRows: [5, 13, 28, 38],
  },
};

async function buildTemplateWorkbook(kind) {
  const t = TEMPLATES[kind];
  if (!t) throw new Error(`No template defined for "${kind}" sheets`);

  const ExcelJS = await excel();
  const wb = new ExcelJS.Workbook();
  wb.creator = 'GIGATT APEX MEFIMS';

  const columns = t.headers.map(({ header, key, width }) => ({ header, key, width }));

  // ── The sheet to fill: header only ──
  const ws = wb.addWorksheet(t.sheetName, { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = columns;

  const head = ws.getRow(1);
  head.height = 24;
  head.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FF0B2452' } } };
  });

  for (const h of t.headers) {
    if (h.money) {
      ws.getColumn(h.key).numFmt = '#,##0.00';
      ws.getColumn(h.key).alignment = { horizontal: 'right' };
    }
  }
  for (const key of t.textColumns || []) ws.getColumn(key).numFmt = '@';

  // ── Example: the same columns, filled in ──
  const ex = wb.addWorksheet('Example (do not fill)');
  ex.columns = columns;
  ex.getRow(1).font = { bold: true };
  t.examples.forEach((r) => ex.addRow(r));
  t.headers.forEach((h, i) => { if (h.money) ex.getColumn(i + 1).numFmt = '#,##0.00'; });

  // ── Guide ──
  const guide = wb.addWorksheet('How to fill this in');
  guide.columns = [{ width: 26 }, { width: 74 }];
  t.guide.forEach((r) => guide.addRow(r));
  guide.getRow(1).font = { bold: true, size: 13, color: { argb: NAVY } };
  (t.boldGuideRows || []).forEach((n) => {
    guide.getRow(n).font = { bold: true, color: { argb: NAVY } };
  });
  guide.eachRow((row) => { row.getCell(2).alignment = { wrapText: true, vertical: 'top' }; });

  return { wb, fileName: t.fileName };
}

export async function downloadTemplate(kind = 'services') {
  const { wb, fileName } = await buildTemplateWorkbook(kind);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick rather than immediately: Safari has not started
  // reading the blob by the time click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
